import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Nav, Tab, Button, Table, Form } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPrint } from 'react-icons/fa';
import DoorTabs from './DoorTabs';
import LineItemTable from './LineItemTable';
import { createBid, getBid, updateBidDate } from '../../services/bidService';
import { getEstimate } from '../../services/estimateService';
import './BidForm.css';

const BidForm = () => {
  const [estimate, setEstimate] = useState(null);
  const [bid, setBid] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [activeDoorTab, setActiveDoorTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingBid, setSavingBid] = useState(false);
  
  const { bidId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const estimateId = queryParams.get('estimateId');
  
  useEffect(() => {
    const loadData = async () => {
      try {
        if (bidId) {
          // Load existing bid
          const bidData = await getBid(bidId);
          setBid(bidData);
          
          // Set first door as active if available
          if (bidData.doors && bidData.doors.length > 0) {
            setActiveDoorTab(bidData.doors[0].id);
          }
          
          // Load estimate info
          const estimateData = await getEstimate(bidData.estimate_id);
          setEstimate(estimateData);
        } else if (estimateId) {
          // Load estimate for new bid
          const estimateData = await getEstimate(estimateId);
          setEstimate(estimateData);
          
          // Create new bid
          const newBid = await createBid(estimateId);
          setBid(newBid);
          
          // Navigate to the bid page to avoid creating multiple bids
          navigate(`/bids/${newBid.id}`, { replace: true });
        } else {
          // No bid or estimate specified
          toast.error('No estimate selected');
          navigate('/estimates');
        }
      } catch (error) {
        console.error('Error loading bid data:', error);
        toast.error('Error loading bid data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [bidId, estimateId, navigate]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  const handleDoorTabChange = (doorId) => {
    setActiveDoorTab(doorId);
  };
  
  const refreshBidData = async () => {
    try {
      const refreshedBid = await getBid(bid.id);
      setBid(refreshedBid);
      return refreshedBid;
    } catch (error) {
      console.error('Error refreshing bid data:', error);
      toast.error('Error refreshing bid data');
    }
  };

  const handleSaveBid = async () => {
    setSavingBid(true);
    try {
      // In a real app, we would save the entire bid state here
      // For this MVP, we'll rely on the individual save operations in LineItemTable
      const refreshedBid = await getBid(bid.id);
      setBid(refreshedBid);
      toast.success('Bid saved successfully');
    } catch (error) {
      console.error('Error saving bid:', error);
      toast.error('Error saving bid');
    } finally {
      setSavingBid(false);
    }
  };
  const handleGenerateReport = () => {
    window.open(`http://localhost:5000/api/bids/${bid.id}/report`, '_blank');
    toast.info('Report generated');
  };
  
  const handleGenerateProposal = () => {
    window.open(`http://localhost:5000/api/bids/${bid.id}/proposal`, '_blank');
    toast.info('Proposal generated');
  };

  const handleBidDateChange = async (e) => {
    const newDate = e.target.value;
    try {
      await updateBidDate(bid.id, newDate);
      setBid({ ...bid, bid_date: newDate });
      toast.success('Bid date updated');
    } catch (error) {
      console.error('Error updating bid date:', error);
      toast.error('Failed to update bid date');
    }
  };

  const handlePrintBid = () => {
    // Create print window
    const printWindow = window.open('', '_blank', 'width=800,height=600');

    // Generate door sections with line items
    const doorSections = bid.doors?.map(door => {
      const lineItemsHtml = door.line_items?.length > 0
        ? door.line_items.map(item => `
          <tr class="line-item-row">
            <td class="part-cell">${item.part_number || ''}</td>
            <td class="desc-cell">${item.description || ''}</td>
            <td class="num-cell">${item.quantity || 0}</td>
            <td class="num-cell">$${(item.price || 0).toFixed(2)}</td>
            <td class="num-cell">${item.labor_hours || 0}</td>
            <td class="num-cell">$${(item.hardware || 0).toFixed(2)}</td>
            <td class="num-cell">$${((item.quantity || 0) * (item.price || 0)).toFixed(2)}</td>
          </tr>
        `).join('')
        : '<tr><td colspan="7" class="no-items">No line items</td></tr>';

      // Build door info string
      const doorInfo = [];
      if (door.door_type) doorInfo.push(`Type: ${door.door_type}`);
      if (door.width && door.height) doorInfo.push(`Size: ${door.width} x ${door.height} ${door.dimension_unit || ''}`);
      if (door.labor_description) doorInfo.push(`Work: ${door.labor_description}`);

      return `
        <div class="door-section">
          <div class="door-header">
            <span class="door-title">Door #${door.door_number} ${door.location ? `- ${door.location}` : ''}</span>
          </div>
          ${doorInfo.length > 0 ? `<div class="door-info">${doorInfo.join(' | ')}</div>` : ''}
          <table class="line-items-table">
            <thead>
              <tr>
                <th class="part-col">Part #</th>
                <th class="desc-col">Description</th>
                <th class="num-col">Qty</th>
                <th class="num-col">Price</th>
                <th class="num-col">Labor Hrs</th>
                <th class="num-col">Hardware</th>
                <th class="num-col">Total</th>
              </tr>
            </thead>
            <tbody>${lineItemsHtml}</tbody>
          </table>
          <div class="door-totals">
            <span>Parts: $${(door.parts_cost || 0).toFixed(2)}</span>
            <span>Labor: $${(door.labor_cost || 0).toFixed(2)}</span>
            <span>Hardware: $${(door.hardware_cost || 0).toFixed(2)}</span>
            <span class="door-total"><strong>Door Total: $${(door.total || 0).toFixed(2)}</strong></span>
          </div>
        </div>
      `;
    }).join('') || '<p>No doors</p>';

    // Generate print content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bid #${bid.id} - ${bid.customer_name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; line-height: 1.3; font-size: 11px; }
          .header { border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 15px; }
          .company-name { font-size: 20px; font-weight: bold; color: #24324d; }
          .bid-title { font-size: 14px; color: #666; margin-top: 5px; }
          .section { margin-bottom: 15px; }
          .section-title { font-size: 13px; font-weight: bold; color: #24324d; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-bottom: 10px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 5px; }
          .info-item { font-size: 11px; }
          .info-item strong { color: #24324d; }
          .door-section { margin-bottom: 15px; page-break-inside: avoid; border: 1px solid #ddd; border-radius: 4px; padding: 10px; }
          .door-header { font-size: 13px; font-weight: bold; color: #24324d; background: #f0f0f0; padding: 6px 10px; margin: -10px -10px 8px -10px; border-radius: 4px 4px 0 0; }
          .door-info { font-size: 10px; color: #666; margin-bottom: 8px; padding: 4px 8px; background: #f9f9f9; border-radius: 3px; }
          .line-items-table { width: 100%; border-collapse: collapse; font-size: 10px; }
          .line-items-table th, .line-items-table td { border: 1px solid #ddd; padding: 4px 6px; }
          .line-items-table th { background: #f5f5f5; font-weight: bold; font-size: 9px; }
          .part-col { width: 12%; text-align: left; }
          .desc-col { width: 35%; text-align: left; }
          .num-col { width: 10%; text-align: right; }
          .part-cell { text-align: left; }
          .desc-cell { text-align: left; white-space: pre-wrap; word-break: break-word; }
          .num-cell { text-align: right; }
          .no-items { text-align: center; color: #999; font-style: italic; }
          .door-totals { display: flex; justify-content: flex-end; gap: 20px; font-size: 10px; padding: 8px 0 0 0; border-top: 1px solid #eee; margin-top: 8px; }
          .door-total { font-weight: bold; color: #24324d; }
          .totals { margin-top: 20px; text-align: right; }
          .totals-grid { display: inline-block; text-align: left; background: #f5f5f5; padding: 15px 20px; border-radius: 4px; }
          .total-row { display: flex; justify-content: space-between; gap: 40px; padding: 4px 0; font-size: 11px; }
          .total-row.grand { font-size: 14px; font-weight: bold; border-top: 2px solid #333; padding-top: 10px; margin-top: 5px; color: #24324d; }
          .footer { margin-top: 30px; text-align: center; font-size: 9px; color: #999; }
          @media print { body { padding: 15px; } @page { margin: 0.5in; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">Scott Overhead Doors</div>
          <div class="bid-title">Bid #BID-${bid.id}${bid.reference_number ? ` | Offer #${bid.reference_number}` : ''}</div>
        </div>

        <div class="section">
          <div class="section-title">Customer Information</div>
          <div class="info-grid">
            <div class="info-item"><strong>Customer:</strong> ${bid.customer_name}</div>
            <div class="info-item"><strong>Bid Date:</strong> ${bid.bid_date ? new Date(bid.bid_date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</div>
            <div class="info-item"><strong>Address:</strong> ${bid.customer_address || 'N/A'}</div>
            <div class="info-item"><strong>Phone:</strong> ${bid.customer_phone || 'N/A'}</div>
            <div class="info-item"><strong>Contact:</strong> ${bid.customer_contact || 'N/A'}</div>
            <div class="info-item"><strong>Status:</strong> ${bid.status?.toUpperCase() || 'DRAFT'}</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Door Details & Line Items</div>
          ${doorSections}
        </div>

        <div class="totals">
          <div class="totals-grid">
            <div class="total-row"><span>Total Parts:</span><span>$${bid.total_parts_cost?.toFixed(2) || '0.00'}</span></div>
            <div class="total-row"><span>Total Labor:</span><span>$${bid.total_labor_cost?.toFixed(2) || '0.00'}</span></div>
            <div class="total-row"><span>Total Hardware:</span><span>$${bid.total_hardware_cost?.toFixed(2) || '0.00'}</span></div>
            <div class="total-row"><span>Tax (8.75%):</span><span>$${bid.tax?.toFixed(2) || '0.00'}</span></div>
            <div class="total-row grand"><span>Grand Total:</span><span>$${bid.total_cost?.toFixed(2) || '0.00'}</span></div>
          </div>
        </div>

        <div class="footer">
          Generated on ${new Date().toLocaleDateString()} | Scott Overhead Doors
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  if (loading) {
    return <div>Loading bid data...</div>;
  }
  
  if (!bid || !estimate) {
    return <div>Error: Bid or estimate data not found</div>;
  }
  
  return (
    <div className="bid-form-container">
      <div className="bid-header">
        <div>
          <h2>Bid for {bid.customer_name}</h2>
          {bid.reference_number && (
            <small className="text-muted">Offer #{bid.reference_number}</small>
          )}
        </div>
        <div className="bid-actions">
          <Button
            variant="secondary"
            onClick={handlePrintBid}
            style={{
              borderRadius: '6px',
              fontWeight: 500,
              border: 'none',
              backgroundColor: '#6c757d',
              color: 'white'
            }}
          >
            <FaPrint size={14} className="me-1" />
            Print
          </Button>
        </div>
      </div>
      
      <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
        <Row>
          <Col sm={12}>
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="info">Customer Info</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="doors">Doors</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="summary">Summary</Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
        </Row>
        
        <Row>
          <Col sm={12}>
            <Tab.Content>
              <Tab.Pane eventKey="info">
                <Card>
                  <Card.Header>Customer Information</Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <p><strong>Customer Name:</strong> {bid.customer_name}</p>
                        <p><strong>Address:</strong> {bid.customer_address || 'N/A'}</p>
                        <p><strong>Estimate ID:</strong> EST-{bid.estimate_id}</p>
                      </Col>
                      <Col md={6}>
                        <p><strong>Contact:</strong> {bid.customer_contact || 'N/A'}</p>
                        <p><strong>Phone:</strong> {bid.customer_phone || 'N/A'}</p>
                        <p><strong>Bid Status:</strong> {bid.status.toUpperCase()}</p>
                      </Col>
                    </Row>
                    <Row className="mt-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label><strong>Bid Date</strong></Form.Label>
                          <Form.Control
                            type="date"
                            value={bid.bid_date || ''}
                            onChange={handleBidDateChange}
                          />
                          <Form.Text className="text-muted">
                            Date the bid was created (editable)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Tab.Pane>
              
              <Tab.Pane eventKey="doors">
              <DoorTabs 
                doors={bid.doors || []} 
                activeDoorId={activeDoorTab}
                onTabChange={handleDoorTabChange}
                onDoorsChanged={refreshBidData}  // Add this new prop
                bidId={bid.id}
              />
                
                {activeDoorTab && (
                  <LineItemTable 
                    doorId={activeDoorTab}
                    door={bid.doors.find(d => d.id === activeDoorTab)}
                    bidId={bid.id}  // MISSING!
                    onUpdate={() => getBid(bid.id).then(newBid => setBid(newBid))}
                  />
                )}
              </Tab.Pane>
              
              <Tab.Pane eventKey="summary">
                <Card>
                  <Card.Header>Cost Summary</Card.Header>
                  <Card.Body>
                    <div className="bid-summary">
                      <div className="summary-item">
                        <span>Total Parts Cost:</span>
                        <span>${bid.total_parts_cost?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="summary-item">
                        <span>Total Labor Cost:</span>
                        <span>${bid.total_labor_cost?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="summary-item">
                        <span>Total Hardware Cost:</span>
                        <span>${bid.total_hardware_cost?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="summary-item">
                        <span>Tax (8.75%):</span>
                        <span>${bid.tax?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="summary-item total">
                        <span>Total Cost:</span>
                        <span>${bid.total_cost?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                    
                    <h5 className="mt-4">Door Summary</h5>
                    <Table striped bordered>
                      <thead>
                        <tr>
                          <th>Door #</th>
                          <th>Parts</th>
                          <th>Labor</th>
                          <th>Hardware</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bid.doors?.map(door => (
                          <tr key={door.id}>
                            <td>{door.door_number}</td>
                            <td>${door.parts_cost?.toFixed(2) || '0.00'}</td>
                            <td>${door.labor_cost?.toFixed(2) || '0.00'}</td>
                            <td>${door.hardware_cost?.toFixed(2) || '0.00'}</td>
                            <td>${door.total?.toFixed(2) || '0.00'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </div>
  );
};

export default BidForm;
